import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box, Typography, Button, Paper, Chip, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Collapse
} from '@material-ui/core'
import {
  CloudDownload as SeedIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore, ExpandLess,
  CheckCircle as CheckIcon
} from '@material-ui/icons'
import axios from 'axios'

// ── Seed Section ─────────────────────────────────────────────

const SeedSection = ({ url }) => {
  const [loading, setLoading] = useState(null) // 'all' | 'services' | 'rules' | 'entity-rates' | 'migrate'
  const [result, setResult] = useState(null)

  const handleSeed = async (type) => {
    setLoading(type)
    setResult(null)
    try {
      let endpoint
      if (type === 'all') endpoint = `${url}/api/bid-estimation/seed-all`
      else if (type === 'services') endpoint = `${url}/api/bid-estimation/seed-expanded-services`
      else if (type === 'services-overwrite') endpoint = `${url}/api/bid-estimation/seed-expanded-services?overwrite=true`
      else if (type === 'entity-rates') endpoint = `${url}/api/bid-estimation/seed-entity-rates`
      else if (type === 'production-rates') endpoint = `${url}/api/bid-estimation/seed-production-rates`
      else if (type === 'production-rates-overwrite') endpoint = `${url}/api/bid-estimation/seed-production-rates?overwrite=true`
      else if (type === 'service-templates') endpoint = `${url}/api/bid-estimation/seed-service-templates`
      else if (type === 'service-templates-overwrite') endpoint = `${url}/api/bid-estimation/seed-service-templates?overwrite=true`
      else if (type === 'migrate') endpoint = `${url}/api/bid-estimation/migrate-rates`
      else if (type === 'migrate-indexes') endpoint = `${url}/api/bid-estimation/migrate-rate-indexes`
      else if (type === 'migrate-equip-roles') endpoint = `${url}/api/bid-estimation/migrate-template-equipment-roles`
      else if (type === 'migrate-material-catalog') endpoint = `${url}/api/bid-estimation/migrate-material-catalog`
      else if (type === 'sync-aspire-catalog') endpoint = `${url}/api/bid-estimation/sync-aspire-catalog`
      else endpoint = `${url}/api/bid-estimation/rate-rules/seed-defaults`

      const res = await axios.post(endpoint)
      setResult(res.data)
    } catch (err) {
      setResult({ error: err.response?.data?.error || err.message })
    } finally {
      setLoading(null)
    }
  }

  return (
    <Paper variant="outlined" style={{ padding: 16, marginBottom: 24 }}>
      <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
        Seed Default Data
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Populate system defaults for expanded services and rate rules. Safe to run multiple times — existing records are skipped.
      </Typography>
      <Box display="flex" style={{ gap: 12 }} alignItems="center">
        <Button
          variant="contained"
          color="primary"
          startIcon={loading === 'all' ? <CircularProgress size={16} color="inherit" /> : <SeedIcon />}
          onClick={() => handleSeed('all')}
          disabled={!!loading}
          size="small"
        >
          Seed All
        </Button>
        <Button
          variant="outlined"
          startIcon={loading === 'services' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('services')}
          disabled={!!loading}
          size="small"
        >
          Services Only
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={loading === 'services-overwrite' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('services-overwrite')}
          disabled={!!loading}
          size="small"
        >
          Re-Seed Services
        </Button>
        <Button
          variant="outlined"
          startIcon={loading === 'rules' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('rules')}
          disabled={!!loading}
          size="small"
        >
          Rate Rules Only
        </Button>
        <Button
          variant="outlined"
          startIcon={loading === 'entity-rates' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('entity-rates')}
          disabled={!!loading}
          size="small"
        >
          Entity Rates
        </Button>
        <Button
          variant="outlined"
          startIcon={loading === 'production-rates' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('production-rates')}
          disabled={!!loading}
          size="small"
        >
          Production Rates (PHX)
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={loading === 'production-rates-overwrite' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('production-rates-overwrite')}
          disabled={!!loading}
          size="small"
        >
          Re-Seed Rates (Overwrite)
        </Button>
        <Button
          variant="outlined"
          startIcon={loading === 'service-templates' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('service-templates')}
          disabled={!!loading}
          size="small"
        >
          Crew Recipes
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={loading === 'service-templates-overwrite' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('service-templates-overwrite')}
          disabled={!!loading}
          size="small"
        >
          Re-Seed Recipes (Overwrite)
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={loading === 'migrate' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('migrate')}
          disabled={!!loading}
          size="small"
        >
          Migrate Legacy Rates
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={loading === 'migrate-indexes' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('migrate-indexes')}
          disabled={!!loading}
          size="small"
        >
          Migrate Rate Indexes
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={loading === 'migrate-equip-roles' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('migrate-equip-roles')}
          disabled={!!loading}
          size="small"
        >
          Migrate Equipment Roles
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={loading === 'migrate-material-catalog' ? <CircularProgress size={16} /> : <SeedIcon />}
          onClick={() => handleSeed('migrate-material-catalog')}
          disabled={!!loading}
          size="small"
        >
          Migrate Material Catalog
        </Button>
        <Button
          variant="contained"
          style={{ backgroundColor: '#1565c0', color: '#fff' }}
          startIcon={loading === 'sync-aspire-catalog' ? <CircularProgress size={16} color="inherit" /> : <SeedIcon />}
          onClick={() => handleSeed('sync-aspire-catalog')}
          disabled={!!loading}
          size="small"
        >
          Sync Aspire Catalog
        </Button>
      </Box>
      {result && !result.error && (
        <Box mt={2} p={1.5} style={{ backgroundColor: '#e8f5e9', borderRadius: 4 }}>
          <Typography variant="body2" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <CheckIcon style={{ color: '#4caf50', fontSize: 18 }} />
            {result.message || `Seeded ${result.seeded} of ${result.total}`}
          </Typography>
          {result.services && (
            <Typography variant="caption" color="textSecondary">
              Services: {result.services.seeded} new / {result.services.total} total · Rules: {result.rules.seeded} new / {result.rules.total} total
            </Typography>
          )}
          {result.migrated != null && (
            <Typography variant="caption" color="textSecondary">
              Migrated: {result.migrated} items · SKU mappings: {result.vendorMappingsCreated} · BidComponent refs: {result.backfilled} · Skipped: {result.skipped} · Errors: {result.errors}
              {result.conflicts && result.conflicts.length > 0 && ` · ${result.conflicts.length} conflict(s)`}
            </Typography>
          )}
        </Box>
      )}
      {result && result.error && (
        <Box mt={2} p={1.5} style={{ backgroundColor: '#ffebee', borderRadius: 4 }}>
          <Typography variant="body2" color="error">{result.error}</Typography>
        </Box>
      )}
    </Paper>
  )
}

// ── Rate Rules Table ─────────────────────────────────────────

const factorColor = (f) => {
  if (f < 1.0) return '#4caf50'
  if (f <= 1.2) return '#ff9800'
  if (f <= 1.5) return '#e65100'
  return '#d32f2f'
}

const RateRulesTable = ({ url }) => {
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [serviceFilter, setServiceFilter] = useState('All')
  const [deleteTarget, setDeleteTarget] = useState(null)

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${url}/api/bid-estimation/rate-rules`)
      setRules(res.data || [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [url])

  useEffect(() => { fetchRules() }, [fetchRules])

  const serviceIds = useMemo(() => {
    const ids = new Set(rules.map(r => r.serviceId))
    return ['All', ...Array.from(ids).sort()]
  }, [rules])

  const filtered = useMemo(() => {
    if (serviceFilter === 'All') return rules
    return rules.filter(r => r.serviceId === serviceFilter)
  }, [rules, serviceFilter])

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await axios.delete(`${url}/api/bid-estimation/rate-rules/${deleteTarget._id}`)
      setDeleteTarget(null)
      fetchRules()
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message))
    }
  }

  if (loading) return <CircularProgress />

  return (
    <Paper variant="outlined">
      <Box p={1.5} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Rate Rules ({filtered.length})
        </Typography>
        <TextField
          select
          size="small"
          variant="outlined"
          value={serviceFilter}
          onChange={(e) => setServiceFilter(e.target.value)}
          style={{ width: 240 }}
        >
          {serviceIds.map(id => (
            <MenuItem key={id} value={id}>
              {id === 'All' ? 'All Services' : id.replace(/^svc_/, '').replaceAll('_', ' ')}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <TableContainer style={{ maxHeight: 500 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell>Rule Name</TableCell>
              <TableCell>Conditions</TableCell>
              <TableCell>Target</TableCell>
              <TableCell align="right">Factor</TableCell>
              <TableCell align="right">+Hours</TableCell>
              <TableCell align="right">+Cost</TableCell>
              <TableCell>Pri</TableCell>
              <TableCell align="right" style={{ width: 60 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(rule => (
              <TableRow key={rule._id} hover>
                <TableCell style={{ fontSize: 12 }}>
                  {rule.serviceId.replace(/^svc_/, '').replaceAll('_', ' ')}
                </TableCell>
                <TableCell style={{ fontSize: 12 }}>{rule.name}</TableCell>
                <TableCell style={{ fontSize: 11 }}>
                  {(rule.conditions || []).map((c, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={`${c.attributeId} ${c.operator} ${Array.isArray(c.value) ? c.value.join(', ') : c.value}`}
                      style={{ fontSize: 10, height: 20, marginRight: 4, marginBottom: 2 }}
                      variant="outlined"
                    />
                  ))}
                </TableCell>
                <TableCell style={{ fontSize: 12 }}>
                  {(rule.effect?.target || '').replaceAll('_', ' ')}
                </TableCell>
                <TableCell align="right">
                  {rule.effect?.factor !== 1.0 && (
                    <Chip
                      size="small"
                      label={`${rule.effect?.factor}x`}
                      style={{
                        backgroundColor: factorColor(rule.effect?.factor || 1),
                        color: '#fff',
                        fontSize: 11,
                        height: 20
                      }}
                    />
                  )}
                </TableCell>
                <TableCell align="right" style={{ fontSize: 12 }}>
                  {rule.effect?.addHours > 0 ? `+${rule.effect.addHours}` : ''}
                </TableCell>
                <TableCell align="right" style={{ fontSize: 12 }}>
                  {rule.effect?.addCost > 0 ? `+$${rule.effect.addCost}` : ''}
                </TableCell>
                <TableCell style={{ fontSize: 12 }}>{rule.priority}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setDeleteTarget(rule)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="textSecondary" variant="body2" style={{ padding: 16 }}>
                    No rate rules found. Use "Seed All" above to load defaults.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Rate Rule?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete "<strong>{deleteTarget?.name}</strong>" for {deleteTarget?.serviceId}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

// ── Attribute Catalog Browser ────────────────────────────────

const AttributeCatalog = ({ url }) => {
  const [attributes, setAttributes] = useState([])
  const [groups, setGroups] = useState({})
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (!url) return
    Promise.all([
      axios.get(`${url}/api/bid-estimation/attributes`),
      axios.get(`${url}/api/bid-estimation/attributes/groups`)
    ]).then(([attrRes, groupRes]) => {
      setAttributes(attrRes.data || [])
      setGroups(groupRes.data || {})
    }).catch(console.error)
  }, [url])

  const typeChipColor = {
    enum: '#1976d2',
    number: '#388e3c',
    integer: '#388e3c',
    boolean: '#7b1fa2',
    string: '#616161'
  }

  return (
    <Paper variant="outlined" style={{ marginTop: 24 }}>
      <Box
        p={1.5}
        display="flex"
        alignItems="center"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ExpandLess /> : <ExpandMore />}
        <Typography variant="subtitle1" style={{ fontWeight: 600, marginLeft: 8 }}>
          Attribute Catalog ({attributes.length} attributes, {Object.keys(groups).length} groups)
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box p={2}>
          {Object.entries(groups).map(([groupName, attrIds]) => {
            const groupAttrs = attrIds
              .map(id => attributes.find(a => a.id === id))
              .filter(Boolean)
            if (groupAttrs.length === 0) return null
            return (
              <Box key={groupName} mb={2}>
                <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#555', marginBottom: 4 }}>
                  {groupName}
                </Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                  {groupAttrs.map(attr => (
                    <Chip
                      key={attr.id}
                      size="small"
                      label={`${attr.name} (${attr.type}${attr.unit ? ', ' + attr.unit : ''})`}
                      style={{
                        backgroundColor: typeChipColor[attr.type] || '#616161',
                        color: '#fff',
                        fontSize: 11
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Collapse>
    </Paper>
  )
}

// ── Main Tab Component ───────────────────────────────────────

const ServiceAttributesTab = ({ url }) => {
  return (
    <Box>
      <SeedSection url={url} />
      <RateRulesTable url={url} />
      <AttributeCatalog url={url} />
    </Box>
  )
}

export default ServiceAttributesTab
